import type { UserItemGrade } from 'bundles/authoring/common/types/gradebookV2';
import type { GradebookEntry } from 'bundles/grading/api/types';

import _t from 'i18n!nls/grading';

/**
 * Get number from decimal to percent for display
 *
 * @param { number } grade
 * @returns number
 */
export const getGradePercentNumberForDisplay = (grade: number): number => {
  return Math.round((grade + Number.EPSILON) * 100) / 100;
};

/**
 * Gets the appropriate text for the grade's tooltip
 *
 * disableGradeMultiplier is false by default but can
 * be set to true if giving whole percent values.
 * Use case is legacy UserItemGrade type which uses
 * whole numbers for grades rather than decimals
 *
 * @param { GradebookEntry | UserItemGrade } gradebookEntry
 * @param { boolean? } disableGradeMultiplier
 * @returns { string }
 */
export const getGradeTooltipText = (
  gradebookEntry: GradebookEntry | UserItemGrade,
  disableGradeMultiplier = false
): string => {
  const multiplier = disableGradeMultiplier ? 1 : 100;
  // Indicate that the grade was overridden. Include the original grade if exists.
  if (gradebookEntry.overrideOutcome) {
    return typeof gradebookEntry.originalOutcome?.grade === 'number'
      ? _t('Grade was overridden from #{originalValue}%.', {
          originalValue: getGradePercentNumberForDisplay(gradebookEntry.originalOutcome.grade * multiplier),
        })
      : _t('Grade was overridden, there is no original released grade.');
  }

  // Indicate whether the grade has a late penalty applied.
  const latePenaltyRatio =
    (gradebookEntry as GradebookEntry).liveOutcome?.latePenaltyRatio?.ratio ||
    (gradebookEntry as GradebookEntry).originalOutcome?.latePenaltyRatio?.ratio ||
    (gradebookEntry as UserItemGrade)?.liveOutcome?.latePenaltyRatio?.definition?.ratio ||
    (gradebookEntry as UserItemGrade)?.originalOutcome?.latePenaltyRatio?.definition?.ratio;

  if (typeof latePenaltyRatio === 'number') {
    return _t('A late penalty of #{latePenaltyPercent}% has been applied.', {
      latePenaltyPercent: getGradePercentNumberForDisplay(latePenaltyRatio * 100),
    });
  } else {
    return _t('Final grade');
  }
};

/**
 * Checks the gradebookEntry object for whether a submitter's
 * grade is hidden to the submitter or not
 *
 * Will return true (hidden) if gradebookEntry.isHidden is null,
 * which can happen when there is no submission for submitter.
 * Currently not possible to have this called when a submitter
 * has no submission but included for linting and as a failsafe
 *
 * @param { GradebookEntry } gradebookEntry
 * @returns { boolean }
 */
export const isGradeHiddenInGradebookEntry = (gradebookEntry: GradebookEntry | null): boolean => {
  return !!gradebookEntry && gradebookEntry.isHidden !== null && gradebookEntry.isHidden !== undefined
    ? gradebookEntry.isHidden
    : true;
};
